
import { LikeButton } from '../../../components/engagement/LikeButton';

interface Release {
  id: string;
  slug: string;
  title: string;
  cover: string;
  price: number;
  type: string;
  releaseDate: string;
  isLimited: boolean;
  copiesLeft?: number;
  totalCopies?: number;
  sales: number;
  tips: number;
  previewTrackId?: number | null;
  previewTrackTitle?: string | null;
  previewTrackDuration?: number | null;
  previewTrackLikes?: number | null;
}

interface ReleaseGridProps {
  releases: Release[];
  sortBy: string;
  onSortChange: (sort: string) => void;
  onBuyRelease: (release: Release) => void;
  onPreviewRelease?: (release: Release) => void;
  currentPreviewTrackId?: string | null;
  isPreviewPlaying?: boolean;
}

export default function ReleaseGrid({
  releases,
  sortBy,
  onSortChange,
  onBuyRelease,
  onPreviewRelease,
  currentPreviewTrackId,
  isPreviewPlaying
}: ReleaseGridProps) {
  const sortedReleases = [...releases].sort((a, b) => {
    switch (sortBy) {
      case 'sales':
        return b.sales - a.sales;
      case 'tips':
        return b.tips - a.tips;
      case 'latest':
      default:
        return new Date(b.releaseDate).getTime() - new Date(a.releaseDate).getTime();
    }
  });

  const formatPrice = (price: number) => (price === 0 ? 'Free' : `KES ${price}`);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  return (
    <section className="bg-white rounded-lg border border-neutral-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-gray-900">Releases</h2>
        
        {/* Sort Tabs */}
        <div className="flex bg-gray-100 rounded-lg p-1">
          <button
            onClick={() => onSortChange('latest')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
              sortBy === 'latest'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Latest
          </button>
          <button
            onClick={() => onSortChange('sales')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
              sortBy === 'sales'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Top Sellers
          </button>
          <button
            onClick={() => onSortChange('tips')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
              sortBy === 'tips'
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Most Tipped
          </button>
        </div>
      </div>

      {/* Releases Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {sortedReleases.map((release) => {
          const previewAvailable = Boolean(release.previewTrackId);
          const isPlaying =
            previewAvailable &&
            currentPreviewTrackId === String(release.previewTrackId) &&
            Boolean(isPreviewPlaying);
          return (
            <div
              key={release.id}
              className="group cursor-pointer"
              onClick={() => onBuyRelease(release)}
            >
            <div className="relative aspect-square mb-3 overflow-hidden rounded-lg">
              <img
                src={release.cover}
                alt={release.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
              />
              
              {/* Badges */}
              <div className="absolute top-2 left-2 flex flex-col gap-1">
                {release.isLimited && (
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                    Limited {release.copiesLeft}/{release.totalCopies}
                  </span>
                )}
              </div>

              {/* Hover Actions */}
              <div className="absolute inset-0 bg-black bg-opacity-40 flex flex-col items-center justify-center gap-3 opacity-0 transition-opacity duration-200 sm:group-hover:opacity-100">
                {previewAvailable && onPreviewRelease && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      onPreviewRelease?.(release);
                    }}
                    className={`bg-white font-semibold py-2 px-4 rounded-lg transition-colors whitespace-nowrap ${
                      isPlaying
                        ? 'text-emerald-700 hover:bg-emerald-50'
                        : 'text-gray-900 hover:bg-gray-100'
                    }`}
                  >
                    <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} mr-2`} />
                    {isPlaying ? 'Pause' : 'Preview'}
                  </button>
                )}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onBuyRelease(release);
                  }}
                  className="bg-white hover:bg-gray-100 text-gray-900 font-semibold py-2 px-4 rounded-lg transition-colors whitespace-nowrap"
                >
                  <i className="ri-shopping-cart-line mr-2"></i>
                  Buy Now
                </button>
              </div>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">
                {release.title}
              </h3>
              <div className="flex items-center justify-between text-sm text-gray-600 mb-2">
                <span>{release.type}</span>
                <span>{formatDate(release.releaseDate)}</span>
              </div>
              {previewAvailable && onPreviewRelease && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    onPreviewRelease(release);
                  }}
                  className={`sm:hidden mb-2 inline-flex items-center gap-2 rounded-full px-3 py-1 text-xs font-semibold transition-colors ${
                    isPlaying
                      ? 'bg-emerald-600 text-white'
                      : 'bg-emerald-100 text-emerald-700'
                  }`}
                >
                  <i className={isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} />
                  {isPlaying ? 'Pause' : 'Preview'}
                </button>
              )}
              <div className="flex items-center justify-between">
                <span className="font-semibold text-emerald-600">
                  {formatPrice(release.price)}
                </span>
                <div className="flex items-center gap-2">
                  {previewAvailable && (
                    <div onClick={(event) => event.stopPropagation()}>
                      <LikeButton
                        subjectType="track"
                        subjectId={Number(release.previewTrackId)}
                        initialCount={release.previewTrackLikes ?? 0}
                        variant="badge"
                        size="sm"
                      />
                    </div>
                  )}
                  {release.tips > 0 && (
                    <div className="text-xs text-gray-500">
                      <span>{release.tips} tips</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {releases.length === 0 && (
        <div className="text-center py-12">
          <i className="ri-music-line text-4xl text-gray-400 mb-4"></i>
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No releases yet</h3>
          <p className="text-gray-600">Follow to get notified when new music drops.</p>
        </div>
      )}
    </section>
  );
}
